import React, { useRef } from "react"
import cx from "classnames"
import { Tooltip } from "../tooltip"
import { useTrackScrollOverflow } from "../use-track-scroll-overflow"

// =============================================================================
// Props
// =============================================================================

export type NavigationMenuProps = {
  /** `Navigation.Item` elements */
  children?: React.ReactNode
}

// =============================================================================
// Styles
// =============================================================================

const menuStyles = [
  "min-h-0 px-2 flex flex-col gap-1 flex-1 overflow-y-auto overflow-x-hidden",
  // Undo global ul styles from application.css
  "list-none mb-0 py-0",
  "transition-shadow"
].join(" ")

// The full class strings are spelled out so Tailwind's JIT can detect them.
const topInsetShadow = "shadow-[inset_0_4px_8px_#1e1e1e0f]"
const bottomInsetShadow = "shadow-[inset_0_-4px_8px_#1e1e1e0f]"
const topAndBottomInsetShadows =
  "shadow-[inset_0_4px_8px_#1e1e1e0f,inset_0_-4px_8px_#1e1e1e0f]"

// =============================================================================
// Component
// =============================================================================

/**
 * Semantic list wrapper for `Navigation.Item` elements.
 * Renders a `<ul>` so screen readers announce the item count and users
 * can navigate the list with assistive technology shortcuts.
 *
 * @example
 * <Navigation.Menu>
 *   <Navigation.Item icon="dashboard" label="Dashboard" pathPrefix="/dashboard" render={<Link href="/dashboard" />} />
 *   <Navigation.Item icon="email" label="Broadcasts" pathPrefix="/broadcasts" render={<Link href="/broadcasts" />} />
 * </Navigation.Menu>
 */
export function NavigationMenu({ children }: NavigationMenuProps) {
  const ref = useRef<HTMLUListElement>(null)
  const { hasTopOverflow, hasBottomOverflow } = useTrackScrollOverflow(ref)

  return (
    <Tooltip.Provider>
      <ul
        ref={ref}
        data-slot="navigation-menu"
        className={cx(menuStyles, {
          [topAndBottomInsetShadows]: hasTopOverflow && hasBottomOverflow,
          [topInsetShadow]: hasTopOverflow && !hasBottomOverflow,
          [bottomInsetShadow]: !hasTopOverflow && hasBottomOverflow
        })}
      >
        {children}
      </ul>
    </Tooltip.Provider>
  )
}

NavigationMenu.displayName = "Navigation.Menu"
